import { create } from 'zustand';

/**
 * 画面に保持する字幕の上限。
 * 1時間超の講義では数百セグメントに達し、全保持だと字幕更新のたびの
 * 配列コピー+再描画コストが線形に増えて端末が固まる(現地で実際に発生)。
 * 溢れた分は捨ててよい — 確定字幕は IndexedDB とサーバーの講義メモに保存済み。
 */
const MAX_LIVE_SEGMENTS = 200;
/**
 * 読み返し中(自動追従オフ)の間の上限。
 * 上限到達のたびに先頭(最古)を1件削るとその行の高さぶん画面がズレ、
 * 「読んでいる最中に勝手にスクロールする」(現地報告 2026-08-12)。
 * 読み返し中は削除を保留し、追従に戻ったら通常上限まで戻す。
 * 500 は保留中の安全弁(これを超えたら読んでいても削る)。
 */
const HOLD_MAX_SEGMENTS = 500;
import type { ProfileId, ServerMessage } from '@cixing/shared';
import type { ConnectionState } from '../ws/client.js';
import { saveSubtitle, type SubtitleRecord } from '../db/idb.js';

/**
 * 字幕1件。partial の間は確定していないため、UI では薄く表示する。
 *
 * segmentId をキーに「差し替え」ていくのが基本方針。追記していくと
 * partial が重複表示され、講義中に読めない画面になる。
 */
export interface Segment {
  segmentId: string;
  sourceZh: string;
  translatedJa: string;
  sourceFinal: boolean;
  translationFinal: boolean;
  startedAt: string;
  /** 再接続直後の最初のセグメント。履歴に区切り線を出すために使う。 */
  afterReconnect?: boolean;
  /**
   * バックグラウンド復帰直後の最初のセグメント。
   * ブラウザの仕様上、他アプリへの切替中はマイクが止まり音声が欠落する。
   * 欠落が「なかったこと」にならないよう、明示の区切りを出す。
   */
  afterBackground?: boolean;
}

interface SessionState {
  segments: Segment[];
  connection: ConnectionState;
  sessionId: string;
  clientSessionId: string;
  glossaryVersion: string;
  phraseCount: number;
  truncatedCount: number;
  profiles: ProfileId[];
  startedAtMs: number | undefined;
  errorMessage: string | undefined;
  /** 次に届くセグメントへ「再接続後」の印を付ける。 */
  pendingReconnectMark: boolean;
  /** 次に届くセグメントへ「バックグラウンド復帰後」の印を付ける。 */
  pendingBackgroundMark: boolean;
  /** 読み返し中(自動追従オフ)は古い字幕の間引きを保留する。 */
  holdTrim: boolean;

  handleServerMessage: (message: ServerMessage) => void;
  setConnection: (state: ConnectionState) => void;
  markReconnected: () => void;
  markBackgroundReturn: () => void;
  beginSession: (profiles: ProfileId[], clientSessionId: string) => void;
  endSession: () => void;
  loadHistory: (records: SubtitleRecord[]) => void;
  clearSegments: () => void;
  dismissError: () => void;
  setHoldTrim: (hold: boolean) => void;
}

export const useSessionStore = create<SessionState>((set, get) => ({
  segments: [],
  connection: 'idle',
  sessionId: '',
  clientSessionId: '',
  glossaryVersion: '',
  phraseCount: 0,
  truncatedCount: 0,
  profiles: [],
  startedAtMs: undefined,
  errorMessage: undefined,
  pendingReconnectMark: false,
  pendingBackgroundMark: false,
  holdTrim: false,

  setHoldTrim: (hold) => set({ holdTrim: hold }),

  beginSession: (profiles, clientSessionId) =>
    set({
      profiles,
      clientSessionId,
      startedAtMs: Date.now(),
      errorMessage: undefined,
    }),

  endSession: () => set({ startedAtMs: undefined, connection: 'closed' }),

  setConnection: (connection) => set({ connection }),

  markReconnected: () => set({ pendingReconnectMark: true }),

  markBackgroundReturn: () => set({ pendingBackgroundMark: true }),

  dismissError: () => set({ errorMessage: undefined }),

  clearSegments: () => set({ segments: [] }),

  loadHistory: (records) =>
    set({
      // ライブ表示と同じ上限を必ず適用する。全件を流し込むと、数週間分の
      // 蓄積(数万件)の一斉レンダリングで端末が固まり、iOS がタブを
      // 強制リロードする無限ループに陥る(2026-08-07 現地で発生)。
      segments: records.slice(-MAX_LIVE_SEGMENTS).map((r) => ({
        segmentId: r.segmentId,
        sourceZh: r.sourceZh,
        translatedJa: r.translatedJa,
        sourceFinal: true,
        translationFinal: true,
        startedAt: r.startedAt,
      })),
    }),

  handleServerMessage: (message) => {
    switch (message.type) {
      case 'session.started':
        set({
          sessionId: message.sessionId,
          glossaryVersion: message.glossaryVersion,
          phraseCount: message.phraseCount,
          truncatedCount: message.truncatedCount,
        });
        return;

      case 'source.partial':
        upsert(set, get, message.segmentId, (s) => ({ ...s, sourceZh: message.text }));
        return;

      case 'source.final':
        upsert(set, get, message.segmentId, (s) => ({
          ...s,
          sourceZh: message.text,
          sourceFinal: true,
        }));
        return;

      case 'translation.partial':
        upsert(set, get, message.segmentId, (s) => ({ ...s, translatedJa: message.text }));
        return;

      case 'translation.final': {
        upsert(set, get, message.segmentId, (s) => ({
          ...s,
          translatedJa: message.text,
          translationFinal: true,
        }));
        // 確定した字幕だけを保存する。partial を保存すると履歴が断片だらけになる。
        const state = get();
        const segment = state.segments.find((s) => s.segmentId === message.segmentId);
        if (segment) {
          void saveSubtitle({
            segmentId: segment.segmentId,
            sessionId: state.sessionId,
            clientSessionId: state.clientSessionId,
            startedAt: segment.startedAt,
            sourceZh: segment.sourceZh,
            translatedJa: message.text,
            profiles: state.profiles,
            glossaryVersion: state.glossaryVersion,
          });
        }
        return;
      }

      case 'error':
        set({ errorMessage: message.message });
        return;

      case 'session.finished':
        set({ startedAtMs: undefined });
        return;

      case 'status':
      case 'usage':
      case 'pong':
        return;
    }
  },
}));

type SetState = (partial: Partial<SessionState>) => void;

/** segmentId で既存を探して差し替える。無ければ末尾に追加する。 */
function upsert(
  set: SetState,
  get: () => SessionState,
  segmentId: string,
  update: (segment: Segment) => Segment,
): void {
  const state = get();
  // 更新対象はほぼ常に直近のセグメント。末尾から探して即ヒットさせる
  // (先頭からの全走査は、長時間講義でフリーズを招いた原因のひとつ)。
  let index = -1;
  for (let i = state.segments.length - 1; i >= 0; i -= 1) {
    if (state.segments[i]!.segmentId === segmentId) {
      index = i;
      break;
    }
  }

  if (index >= 0) {
    const next = [...state.segments];
    next[index] = update(next[index]!);
    set({ segments: next });
    return;
  }

  const created: Segment = update({
    segmentId,
    sourceZh: '',
    translatedJa: '',
    sourceFinal: false,
    translationFinal: false,
    startedAt: new Date().toISOString(),
    ...(state.pendingReconnectMark ? { afterReconnect: true } : {}),
    ...(state.pendingBackgroundMark ? { afterBackground: true } : {}),
  });

  // 上限超過分は先頭(最古)から捨てる。講義メモ側に保存済みなので失われない。
  // 読み返し中(holdTrim)は捨てると画面がズレるため保留する(安全弁あり)。
  const max = state.holdTrim ? HOLD_MAX_SEGMENTS : MAX_LIVE_SEGMENTS;
  const appended = [...state.segments, created];
  const trimmed = appended.length > max ? appended.slice(appended.length - max) : appended;

  set({
    segments: trimmed,
    ...(state.pendingReconnectMark ? { pendingReconnectMark: false } : {}),
    ...(state.pendingBackgroundMark ? { pendingBackgroundMark: false } : {}),
  });
}
